import { useFormDispatch } from '@elfsight/widget-form-builder/widget';
import { useRegisterAPIService } from '@elfsight/widget-js-api';
import type { API_SUBMISSION_FORM_SERVICE_NAME } from '../constants';
import {
  API_CALCULATOR_FORM_SERVICE_NAME,
  API_SET_VALUE_METHOD_NAME
} from '../constants';
import type { CalculatorField, CustomFormField } from '../types';
import { validateAPIValue, validateCalcAPIValue } from './form/utils';

function submissionFormFieldFinder(
  targetId: string,
  sourceFields: CustomFormField[]
) {
  return sourceFields.find(
    ({ fieldID, id: itemId }) =>
      `[${fieldID}]` === targetId || itemId === targetId
  );
}

function calcFormFieldFinder(
  targetId: string,
  sourceFields: CalculatorField[],
  sourceFieldsIDs: Record<string, string>
) {
  return sourceFields.find(
    ({ id: itemId }) =>
      (itemId && `[${sourceFieldsIDs[itemId]}]` === targetId) ||
      itemId === targetId
  );
}

export type CalcFormServiceRegistrarProps = {
  serviceName: typeof API_CALCULATOR_FORM_SERVICE_NAME;
  fields: CalculatorField[];
  fieldsIDs: Record<string, string>;
};

export type SubmissionFormServiceRegistrarProps = {
  serviceName: typeof API_SUBMISSION_FORM_SERVICE_NAME;
  fields: CustomFormField[];
  fieldsIDs?: undefined;
};

type FormServiceRegistrarProps =
  | CalcFormServiceRegistrarProps
  | SubmissionFormServiceRegistrarProps;

function getDispatchFieldValue(
  targetId: string,
  targetValue: any,
  { serviceName, fields, fieldsIDs }: FormServiceRegistrarProps
) {
  let field, validationParams;

  if (serviceName === API_CALCULATOR_FORM_SERVICE_NAME) {
    field = calcFormFieldFinder(targetId, fields, fieldsIDs);

    if (!field) {
      return;
    }

    validationParams = validateCalcAPIValue(targetValue, field);
  } else {
    field = submissionFormFieldFinder(targetId, fields);

    if (!field) {
      return;
    }

    validationParams = validateAPIValue(targetValue, field);
  }

  if (field.id && validationParams) {
    const [derivedValue, isValid] = validationParams;

    return { id: field.id, value: derivedValue, valid: isValid };
  }

  return;
}

export function useRegisterFormService(props: FormServiceRegistrarProps) {
  const dispatch = useFormDispatch();
  const { serviceName } = props;

  useRegisterAPIService(
    serviceName,
    () => ({
      [API_SET_VALUE_METHOD_NAME]: (id, value) => {
        if (value === null || value === undefined) {
          return;
        }

        const dispatchFieldValue = getDispatchFieldValue(id, value, props);

        if (dispatchFieldValue) {
          dispatch({
            type: 'changeField',
            field: dispatchFieldValue
          });
        }
      }
    }),
    [dispatch, props]
  );
}
